import { acceptHMRUpdate, defineStore } from 'pinia'
import { ReturnFormat } from "@/enums"
import { log } from "@/services"
import { FormbuilderForm, FormbuilderQuestion } from "@/types"

interface State {
    received: null | FormbuilderForm,
    formState: FormbuilderQuestion[],
    errors: Map<string, string>,
    warnings: Map<string, string>,
}

export const useFormStore = defineStore('formstore', {
    state: (): State => ({
        received: null,
        formState: [],
        errors: new Map(),
        warnings: new Map(),
    }),
    actions: {
        receive(newData: FormbuilderForm | null) {
            this.received = newData || {} as FormbuilderForm
            this.formState = []
            if (newData === null) return
            let entryQuestion = this.getQuestion(newData.entryQuestion)
            if (entryQuestion === null) return
            this.formState.push(entryQuestion)
            let nq = this.getQuestion(entryQuestion.next)
            while (nq !== null) {
                this.formState.push(nq)
                nq = this.getQuestion(this.formState[this.formState.length - 1].next)
            }

        },
        changeNextQuestion({ question = null as string | null , next = null as string | null }) {
            log.debug("Update question ", question, "with next :", next)
            for (let [i, q] of this.formState.entries()) {
                if (question === q.id) {
                    this.formState.length = i+1
                    log.debug("Trimmed formState after question", question)
                    q.next = next
                }
            }
            let nextQuestion = this.getQuestion(next)
            while (nextQuestion) {
                this.formState.push(nextQuestion)
                let lastQuestion = this.formState[this.formState.length - 1]
                if (!lastQuestion.next || lastQuestion.next === "submit") break
                nextQuestion = this.getQuestion(lastQuestion.next)
            }
            log.debug("New formstate", this.formState)
        },
        updateErrors({ errors = {} as Map<string, string>, warnings = {} as Map<string, string> }) {
            this.errors = errors
            this.warnings = warnings
        },
        getEntryQuestion() {
            if (this.received) return this.received.questions.get(this.received.entryQuestion)
            return null
        },
        getReturnFormat() {
            if (!this.received) return null
            if (this.received.returnFormat === ReturnFormat.formData) return ReturnFormat.formData
            return ReturnFormat.json
        },
        getQuestion(id: string | null | undefined): FormbuilderQuestion | null {
            if (!id) return null
            if (!this.received?.questions) return null
            return this.received.questions.get(id) as FormbuilderQuestion
        },
        getError(questionId: string) {
            if (!this.errors.get(questionId)) return false
            return this.errors.get(questionId)
        },
        getWarning(questionId: string) {
            if (!this.warnings.get(questionId)) return false
            return this.warnings.get(questionId)
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFormStore, import.meta.hot))
}
